import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Hand, ClipboardCheck, Undo2 } from "lucide-react";

const STAGES = ["New Lead", "App", "Pre-Approval", "Satisfying Conditions", "Clear to Close", "Closed"];

export const ClaimButton = ({ l, onDone }) => {
  const [busy, setBusy] = useState(false);
  const claim = async () => {
    setBusy(true);
    try { await api.post(`/live-transfers/leads/${l.id}/claim`); toast.success("Lead claimed - it's yours now"); onDone(); }
    catch (e) { toast.error(apiError(e)); onDone(); } finally { setBusy(false); }
  };
  return (
    <Button size="sm" className="h-7 gold-cta border-0 font-bold" onClick={claim} disabled={busy} data-testid={`lt-lead-claim-${l.id}`}>
      <Hand className="mr-1 h-3.5 w-3.5" />{busy ? "Claiming..." : "Claim lead"}
    </Button>
  );
};

const Deadline = ({ l }) => {
  if (!l.lt_deadline || (l.lt_disposition || "New Lead") !== "New Lead") return null;
  return (
    <div className="mt-1 text-xs font-medium text-amber-700" data-testid={`lt-lead-deadline-${l.id}`}>
      Move to App or write an explanation by {new Date(l.lt_deadline).toLocaleString()}, or it returns to the Marketplace.
    </div>
  );
};

export const ReleaseButton = ({ l, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/live-transfers/leads/${l.id}/release`);
      toast.success(r.data.credit ? `Released - $${r.data.credit} added to your Live Transfers balance` : "Released to the Marketplace");
      setOpen(false); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="ml-2 mt-1 h-7 border-amber-300 text-amber-800" onClick={() => setOpen(true)} data-testid={`lt-lead-release-btn-${l.id}`}>
        <Undo2 className="mr-1 h-3.5 w-3.5" />Release to Marketplace
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={`lt-lead-release-dialog-${l.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Release {l.name || "this lead"} to the Marketplace?</AlertDialogTitle>
            <AlertDialogDescription>The lead leaves your account right away and is offered to other loan officers on the Marketplace. If you paid for this live transfer, $20 is added to your Live Transfers balance. Free or claimed leads get no credit. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`lt-lead-release-cancel-${l.id}`}>Keep lead</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); go(); }} disabled={busy} data-testid={`lt-lead-release-confirm-${l.id}`}>{busy ? "Releasing..." : "Release lead"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const Disposition = ({ l, onDone }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(l.lt_disposition || "New Lead");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/live-transfers/leads/${l.id}/disposition`, { status, explanation: note }); toast.success("Status updated"); setOpen(false); setNote(""); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2">
      <Deadline l={l} />
      {!open ? (
        <Button size="sm" variant="outline" className="mt-1 h-7" onClick={() => { setStatus(l.lt_disposition || "New Lead"); setOpen(true); }} data-testid={`lt-lead-disposition-btn-${l.id}`}>
          <ClipboardCheck className="mr-1 h-3.5 w-3.5" />Disposition
        </Button>
      ) : (
        <div className="mt-1 space-y-2 rounded-md border border-slate-200 p-2" data-testid={`lt-lead-disposition-form-${l.id}`}>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 w-full sm:w-64" data-testid={`lt-lead-disposition-select-${l.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s} data-testid={`lt-stage-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000}
            placeholder={status === "New Lead" ? "Required: why hasn't this lead become an app yet? (gives you one more week)" : "Optional note"}
            data-testid={`lt-lead-disposition-note-${l.id}`} />
          <div className="flex gap-2">
            <Button size="sm" className="h-7" onClick={save} disabled={busy || (status === "New Lead" && note.trim().length < 10)} data-testid={`lt-lead-disposition-save-${l.id}`}>{busy ? "Saving..." : "Save"}</Button>
            <Button size="sm" variant="ghost" className="h-7" onClick={() => setOpen(false)} data-testid={`lt-lead-disposition-cancel-${l.id}`}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const Explanations = ({ l }) => {
  if (!l.lt_explanations?.length) return null;
  return (
    <div className="mt-1 space-y-0.5 text-xs text-slate-600" data-testid={`lt-lead-explanations-${l.id}`}>
      {l.lt_explanations.map((x, i) => <div key={i}><span className="text-slate-400">{new Date(x.at).toLocaleDateString()}:</span> {x.text}</div>)}
    </div>
  );
};
